// ============================================
// File: src/services/cognitoAuth.js
//
// Passwordless (phone + SMS OTP) student authentication against the Cognito
// student pool, using Cognito's NATIVE passwordless flow (USER_AUTH +
// SMS_OTP challenge, launched Nov 2024). Cognito owns code generation,
// delivery (via SNS), attempt limiting, and expiry — no custom Lambda
// triggers and no third-party call in the authentication path.
//
// Zero dependencies — the Cognito IDP API is called directly with fetch
// (InitiateAuth is an unauthenticated, unsigned operation for user pools).
// This keeps the SPA bundle free of amplify/amazon-cognito-*.
//
// .env additions:
//   REACT_APP_COGNITO_REGION=eu-west-2
//   REACT_APP_COGNITO_CLIENT_ID=<StudentAppClient id from the CF stack>
//
// Token storage: localStorage under drivezoe_student_* keys. The ACCESS
// token is what gets sent to the backend (the authAdapter validates
// tokenUse 'access' and reads custom:studentId from it).
// ============================================

const REGION = process.env.REACT_APP_COGNITO_REGION || 'eu-west-2'
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '118s9et1goot70kvo0b52cmmn'
const ENDPOINT = `https://cognito-idp.${REGION}.amazonaws.com/`

const KEYS = {
  access: 'drivezoe_student_access_token',
  refresh: 'drivezoe_student_refresh_token',
  expires: 'drivezoe_student_token_expires_at',
  username: 'drivezoe_student_cognito_username',
  phone: 'drivezoe_student_phone',
};

async function cognitoCall(target, body) {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const type = (data.__type || '').split('#').pop();
    const err = new Error(data.message || type || 'Authentication error');
    err.code = type;
    throw err;
  }
  return data;
}

/** UK-friendly E.164 normalisation (mirrors backend smsService.normalisePhone) */
export function toE164(phone) {
  let digits = phone.replace(/[^\d+]/g, '');
  if (digits.startsWith('00')) digits = '+' + digits.slice(2);
  if (/^\d{10,11}$/.test(digits) && digits.startsWith('0')) {
    digits = '+44' + digits.slice(1);
  }
  if (!digits.startsWith('+')) digits = '+' + digits;
  return digits;
}

/**
 * Step 1 — start native Cognito passwordless sign-in. Triggers Cognito's
 * own SMS OTP send (via SNS under the hood) — no custom Lambda in the path.
 * Returns { session, username, maskedPhone } for the OTP step.
 */
export async function startStudentLogin(phone) {
  const phoneE164 = toE164(phone);
  console.log('CLIENT_ID', CLIENT_ID);
  console.log('PHONE', phone, phoneE164);
  const data = await cognitoCall('InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: {
      USERNAME: phoneE164,
      PREFERRED_CHALLENGE: 'SMS_OTP', // skips the AvailableChallenges round trip
    },
  });

  if (data.ChallengeName !== 'SMS_OTP') {
    throw new Error('Unexpected authentication response');
  }
  return {
    session: data.Session,
    // Cognito resolves the phone alias to the internal UUID username —
    // ALL subsequent RespondToAuthChallenge calls must use that, not the phone.
    username: data.ChallengeParameters?.USERNAME || phoneE164,
    maskedPhone:
      data.ChallengeParameters?.CODE_DELIVERY_DESTINATION || phoneE164,
    phoneE164,
  };
}

/**
 * Step 2 — answer the OTP. On a wrong code Cognito either re-issues the
 * SMS_OTP challenge with a fresh session (surfaced as { retry: true }) or,
 * after its own attempt limit, throws — surfaced as a thrown error so the
 * caller restarts from the phone step.
 * On success, tokens are persisted and { success: true } is returned.
 */
export async function confirmStudentOtp({ session, username, code, phoneE164 }) {
  const data = await cognitoCall('RespondToAuthChallenge', {
    ChallengeName: 'SMS_OTP',
    ClientId: CLIENT_ID,
    Session: session,
    ChallengeResponses: { USERNAME: username, SMS_OTP_CODE: code.trim() },
  });

  if (data.AuthenticationResult) {
    const { AccessToken, RefreshToken, ExpiresIn } = data.AuthenticationResult;
    localStorage.setItem(KEYS.access, AccessToken);
    if (RefreshToken) localStorage.setItem(KEYS.refresh, RefreshToken);
    // 60s safety margin so we refresh before, not after, expiry
    localStorage.setItem(
      KEYS.expires,
      String(Date.now() + (ExpiresIn - 60) * 1000)
    );
    localStorage.setItem(KEYS.username, username);
    if (phoneE164) localStorage.setItem(KEYS.phone, phoneE164);
    return { success: true };
  }

  if (data.ChallengeName === 'SMS_OTP') {
    return { retry: true, session: data.Session };
  }
  throw new Error('Unexpected authentication response');
}

/** Silent refresh using the 90-day refresh token. */
async function refreshTokens() {
  const refreshToken = localStorage.getItem(KEYS.refresh);
  if (!refreshToken) throw new Error('No refresh token');

  const data = await cognitoCall('InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: CLIENT_ID,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  const { AccessToken, ExpiresIn } = data.AuthenticationResult;
  localStorage.setItem(KEYS.access, AccessToken);
  localStorage.setItem(
    KEYS.expires,
    String(Date.now() + (ExpiresIn - 60) * 1000)
  );
  return AccessToken;
}

/**
 * The getToken for student sessions — same contract as Auth0's
 * getAccessTokenSilently: returns a valid access token, refreshing
 * transparently when close to expiry. Throws when signed out.
 */
export async function getStudentToken() {
  const access = localStorage.getItem(KEYS.access);
  const expires = Number(localStorage.getItem(KEYS.expires) || 0);
  if (access && Date.now() < expires) return access;
  return refreshTokens();
}

export function isStudentSession() {
  return !!localStorage.getItem(KEYS.refresh);
}

export function getStudentPhone() {
  return localStorage.getItem(KEYS.phone);
}

/**
 * Best-effort display name from the stored access token's `name` claim
 * (populated by the backend's Pre Token Generation trigger — see
 * authAdapter.js). Purely cosmetic (e.g. dashboard greeting) — never
 * verifies the token, so never trust this for anything security-relevant.
 * Returns null on any decode failure or if the claim is absent.
 */
export function getStudentName() {
  try {
    const token = localStorage.getItem(KEYS.access);
    if (!token) return null;
    const payloadB64 = token.split('.')[1];
    const payload = JSON.parse(
      atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))
    );
    return payload.name || null;
  } catch {
    return null;
  }
}

export function studentLogout() {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
}
